/**
 * Dialog title bar with Save/Cancel at the top-right — mirrors the Access form
 * header (btnSave/btnCancel in FormHeader). Shared by the Report Inputs dialogs
 * (Exposure + Cash Flow) so the action buttons sit top-right, not in a bottom
 * DialogActions. Pattern copied from `StackFormDialog`.
 */
import { Box, Button, CircularProgress, Divider, DialogTitle, Typography } from '@mui/material'
import type { ReactNode } from 'react'
import { HIDE_ON_MOBILE_INLINE, TEXT_TRUNCATE } from '../theme/responsive'

const titleBtnSx = {
  py: 0.25,
  px: 1,
  minHeight: 28,
  fontSize: '0.75rem',
  lineHeight: 1.2,
  textTransform: 'none' as const,
}

interface Props {
  title: ReactNode
  onSave: () => void
  onCancel: () => void
  saving?: boolean
  saveDisabled?: boolean
  saveLabel?: string
  /** Extra action buttons rendered left of Save (e.g. Copy/Delete). */
  extraActions?: ReactNode
}

export default function DialogTitleBar({
  title,
  onSave,
  onCancel,
  saving = false,
  saveDisabled = false,
  saveLabel = 'Save',
  extraActions,
}: Props) {
  return (
    <>
      <DialogTitle sx={{ pb: 1, pt: 1.25, display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
        <Typography
          component="span"
          noWrap
          sx={{ ...TEXT_TRUNCATE, flex: '1 1 auto', fontWeight: 700, fontSize: '1rem', minWidth: 0 }}
        >
          {title}
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', ml: 'auto', flexShrink: 0 }}>
          {extraActions}
          <Button
            variant="contained"
            size="small"
            color="success"
            onClick={onSave}
            disabled={saveDisabled}
            sx={titleBtnSx}
          >
            {saving ? <CircularProgress size={14} color="inherit" /> : saveLabel}
          </Button>
          <Button variant="outlined" size="small" onClick={onCancel} disabled={saving} sx={titleBtnSx}>
            <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>
              Cancel
            </Box>
            <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
              ✕
            </Box>
          </Button>
        </Box>
      </DialogTitle>
      <Divider sx={{ flexShrink: 0 }} />
    </>
  )
}
